import { useEffect, useState, useRef } from 'react';
import './SelectionTimer.css';

interface SelectionTimerProps {
  timeRemaining: number; // seconds remaining from server
  totalDuration: number; // total duration in seconds (e.g., 60)
  speedMultiplier?: number; // speed multiplier (1, 2, or 3)
}

export function SelectionTimer({ timeRemaining, totalDuration, speedMultiplier = 1 }: SelectionTimerProps) {
  const [displayTime, setDisplayTime] = useState(timeRemaining);
  const intervalRef = useRef<number | null>(null);
  const lastServerTimeRef = useRef(timeRemaining);
  const lastSyncTimestampRef = useRef(Date.now());

  // Sync with server updates
  useEffect(() => {
    // Server sent us an update - only sync if there's actual drift
    const timeDrift = Math.abs(displayTime - timeRemaining);
    
    // If drift is more than 2 seconds, hard sync to server time
    if (timeDrift > 2) {
      console.log(`⏰ Timer sync: Client ${displayTime}s, Server ${timeRemaining}s (drift: ${timeDrift}s) - hard sync`);
      setDisplayTime(timeRemaining);
    }
    // DON'T sync for small drift - let client timer run smoothly
    
    lastServerTimeRef.current = timeRemaining;
    lastSyncTimestampRef.current = Date.now();
  }, [timeRemaining, displayTime]);

  // Client-side countdown - ticks every second
  useEffect(() => {
    // Clear any existing interval
    if (intervalRef.current !== null) {
      window.clearInterval(intervalRef.current);
    }

    // Start client-side countdown that runs continuously
    intervalRef.current = window.setInterval(() => {
      setDisplayTime((prev) => {
        // Don't go below 0
        if (prev <= 0) {
          return 0;
        }
        
        const newTime = Math.max(0, prev - 1);
        
        // Log if we've drifted significantly from expected server time
        const elapsedSinceSync = (Date.now() - lastSyncTimestampRef.current) / 1000;
        const expectedServerTime = Math.max(0, lastServerTimeRef.current - elapsedSinceSync);
        const drift = Math.abs(newTime - expectedServerTime);
        
        if (drift > 3) {
          console.log(`⚠️ Timer drift detected: ${drift.toFixed(1)}s (waiting for server sync...)`);
        }
        
        return newTime;
      });
    }, 1000);

    // Cleanup on unmount
    return () => {
      if (intervalRef.current !== null) {
        window.clearInterval(intervalRef.current);
      }
    };
  }, []); // Empty dependency array - only create interval once!

  // Calculate urgency level
  const getUrgencyClass = () => {
    if (displayTime > totalDuration * 0.5) return 'timer-safe';
    if (displayTime > totalDuration * 0.33) return 'timer-warning';
    return 'timer-danger';
  };

  // Format time as MM:SS
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const urgencyClass = getUrgencyClass();
  const shouldPulse = displayTime <= 10;
  const speedClass = speedMultiplier > 1 ? `timer-speed-${speedMultiplier}x` : '';

  return (
    <div className={`selection-timer ${urgencyClass} ${shouldPulse ? 'timer-pulse' : ''} ${speedClass}`}>
      <div className="timer-label">
        Time Remaining
        {speedMultiplier > 1 && <span className="speed-indicator"> ({speedMultiplier}x)</span>}
      </div>
      <div className="timer-display">
        {formatTime(displayTime)}
      </div>
      <div className="timer-bar">
        <div 
          className="timer-bar-fill"
          style={{ width: `${(displayTime / totalDuration) * 100}%` }}
        />
      </div>
    </div>
  );
}

